import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import moment from 'moment';

import {
    Button,
    Container,
    Divider,
    Grid,
    Header,
    Image,
    List,
    Segment,
    Sticky,
} from 'semantic-ui-react';
import Layout from './layout';

import ArticleHeader from './ArticleHeader';
import Helmet from './Helmet';
import AdvertiserDisclosurePopUp from './Disclosures/AdvertiserPopUp';
import LazyLoadImage from './LazyLoadImage';

const Avatar = styled(Image)`
    float: left;
    border: 1px solid white;
    height: 50px;
    width: 50px;
    margin-right: 10px
`;

const FeaturedImage = styled(Container)`
    margin-left: auto;
    margin-right: auto;
    margin-top: 1em;
    
    .ui.image {
        width: 100%;
        max-width: 700px;
    }
`;

const PostMeta = styled(Segment)`
    &&&&& {
        display: inline-block;
        width: 100%;
        padding-top: 0.25em;
        margin-bottom: 0.5em;
    }
`;
PostMeta.List = styled(List)`&&&&& {
    width: 100%;
    @media only screen and (max-width: 479px) {
        padding-top: 1em;
        font-size: 0.6em;
    }
}`;
PostMeta.List.Author = styled(List.Item)`
    float: left;
    clear: left;
`;
PostMeta.List.Dates = styled(List.Item)`
    float: right;
    padding-top: 0;
    text-align: right;
    font-size: 0.85em;
`;

PostMeta.AdvertiserDisclosurePopUp = styled(Segment)`
    &&&&& {
       border: 0 solid transparent;
       box-shadow: none;
       padding: 0;
       margin-bottom: 0.5em;
    }
`;

const SidebarLeft = styled(Grid.Column)`
    &&&&& {
        padding-top: 6em;
        margin: 0;
    }
`;

const SidebarRight = styled(Grid.Column)`
    &&&&& {
        padding-top: 6em;
        padding-left: 0;
        margin: 0 0 0 1rem;
    }
`;

const SidebarCta = styled(Segment)`
    &&&&& {
        border: none;
        border-top: 1em #084E8A solid;
        svg {
            fill: #084E8A;
            width: 4em;
            height: auto;
        }
    }
`;

const ArticleCta = styled(SidebarCta)`
    &&&&& {
        svg {
            width: 6em;
        }
        .header {
            font-size: xx-large;
        }
    }
`;

const SidebarEducationCenter = styled(Segment)`
    &&&&& {
        padding-left: 0;
        padding-right: 0;
    }
`;

export default class Post extends React.Component {
    state = {};

    static propTypes = {
        article: PropTypes.shape({
            slug: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            excerpt: PropTypes.string.isRequired,
            date: PropTypes.string.isRequired,
            modified: PropTypes.string,
            featured_image: PropTypes.shape({
                source_url: PropTypes.string,
            }),
            categories: PropTypes.shape({
                name: PropTypes.string,
                slug: PropTypes.string,
            }),
            acf: PropTypes.shape({
                seo_title: PropTypes.string,
                robots: PropTypes.string,
            }),
            read_time_minutes: PropTypes.number,
            content: PropTypes.node,
        }).isRequired,
    }

    handleStickyContextRef = contextRef => this.setState({ contextRef });

    render() {
        const { contextRef } = this.state;
        const {
            article,
        } = this.props;


        return (
            <Layout>
                <Helmet
                    canonicalUrl={article.slug}
                    title={article.title}
                    description={article.excerpt}
                    image={article.featured_image.source_url}
                    robots={article.acf.robots}
                    isArticle
                    datePublished={article.date}
                    dateModified={article.modified}
                    author={{
                        name: 'DebtConsolidation Team',
                        image: '/assets/images/authors/team.png',
                    }}
                />
                <FeaturedImage>
                    <LazyLoadImage src={article.featured_image.source_url} alt={article.title} />
                </FeaturedImage>
                <Container>
                    <div ref={this.handleStickyContextRef}>
                        <Grid columns="equal">
                            <SidebarLeft only="large screen">
                                <Sticky offset={20} context={contextRef}>
                                    <List>
                                        <List.Item>
                                            <Button
                                                aria-label="twitter"
                                                color="twitter"
                                                icon="twitter"
                                                size="huge"
                                                href="https://twitter.com/officialdc_com"
                                            />
                                        </List.Item>
                                        <List.Item>
                                            <Button
                                                aria-label="facebook"
                                                color="facebook"
                                                icon="facebook f"
                                                size="huge"
                                                href="https://www.facebook.com/debtconsolidationofficial/"
                                            />
                                        </List.Item>
                                    </List>
                                </Sticky>
                            </SidebarLeft>
                            <Grid.Column as="article" className="article-main">
                                <PostMeta basic clearing as="header">
                                    <PostMeta.AdvertiserDisclosurePopUp>
                                        <AdvertiserDisclosurePopUp position="bottom left" />
                                    </PostMeta.AdvertiserDisclosurePopUp>
                                    <Avatar circular>
                                        <Image fluid src="/assets/images/authors/team.png" />
                                    </Avatar>
                                    <PostMeta.List size="small">
                                        <PostMeta.List.Author>
                                            <List.Content>
                                                <List.Header>
                                                    By
                                                    {' '}
                                                    <a href="/about/">DebtConsolidation Team</a>
                                                </List.Header>
                                                <List.Description>
                                                    DebtConsolidation.com
                                                </List.Description>
                                            </List.Content>
                                        </PostMeta.List.Author>
                                        <PostMeta.List.Dates>
                                            <List.Content>
                                                <List.Description>
                                                    {`Published: ${moment(article.date)
                                                        .format('ll')}`}
                                                    <br />
                                                    {`Updated: ${moment(article.modified)
                                                        .format('ll')}`}
                                                </List.Description>
                                            </List.Content>
                                        </PostMeta.List.Dates>
                                    </PostMeta.List>
                                </PostMeta>
                                <ArticleHeader
                                    title={article.title}
                                    readTimeMinutes={article.read_time_minutes}
                                />
                                {article.content}
                            </Grid.Column>
                            <SidebarRight only="computer" />
                        </Grid>
                    </div>
                </Container>
                <Divider hidden />
                <Container text>
                    <Segment basic textAlign="center">
                        <List horizontal>
                            <List.Item>
                                <Button
                                    aria-label="twitter"
                                    color="twitter"
                                    icon="twitter"
                                    size="huge"
                                    href="https://twitter.com/officialdc_com"
                                />
                            </List.Item>
                            <List.Item>
                                <Button
                                    aria-label="facebook"
                                    color="facebook"
                                    icon="facebook f"
                                    size="huge"
                                    href="https://www.facebook.com/debtconsolidationofficial/"
                                />
                            </List.Item>
                        </List>
                    </Segment>
                </Container>
                <Divider hidden />
                <Container text className="wide">
                    <Segment basic>
                        <Header>Learn About Debt</Header>
                        <List relaxed className="featured-articles footer">
                            <List.Item>
                                <a href="/6-steps-out-of-debt/">
                                    The Simple 6-Step Process To Getting Out of Debt
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href="/stop-debt-collector-calls/">
                                    How To Permanently Stop Debt Collectors From Calling You
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href="/understanding-debt-consolidation/">
                                    Understanding Debt Consolidation: What It Does and How It Can Help
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href="/how-to-get-out-of-debt/">How to Get Out of Debt</a>
                            </List.Item>
                        </List>
                    </Segment>
                </Container>
            </Layout>
        );
    }
}
